import { useLayoutEffect, useRef, type CSSProperties } from 'react';

/**
 * Text that shrinks to fit its box (Cole, 2026-10-18: Football's drive table
 * spells results out, "TURNOVER ON DOWNS", in a narrow cell). It may wrap
 * onto two lines; past that the font steps down until nothing overflows,
 * down to a floor of 35% of the stylesheet's size. Short text is untouched.
 */
export default function FitText({ text, className, style }: { text: string; className?: string; style?: CSSProperties }) {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.fontSize = '';
      const base = parseFloat(getComputedStyle(el).fontSize) || 16;
      const box = el.parentElement;
      if (!box) return;
      const lineHeight = base * 1.05;
      for (let scale = 1; scale >= 0.35; scale -= 0.05) {
        el.style.fontSize = `${base * scale}px`;
        const fitsWidth = el.scrollWidth <= box.clientWidth + 0.5;
        const fitsLines = el.offsetHeight <= lineHeight * scale * 2 + 1;
        if (fitsWidth && fitsLines) return;
      }
    };
    fit();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    if (observer && el.parentElement) observer.observe(el.parentElement);
    return () => observer?.disconnect();
  }, [text]);
  return (
    <b ref={ref} className={className} style={{ ...style, lineHeight: 1.05, textAlign: 'center', maxWidth: '100%', overflowWrap: 'normal' }}>
      {text}
    </b>
  );
}
